import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { QueryProvider } from "@finitless/shared/providers";
import {
  ToastProvider,
  BrandProvider,
  AuthProvider,
  useAuth,
  GlobalConfigProvider,
  WebsocketProvider,
  IntercomProvider,
  PostHogProvider,
} from "@finitless/shared/contexts";
import { Socket } from "@finitless/shared/lib";
import {
  useToast,
  useIntercom,
  useGlobalConfigs,
} from "@finitless/shared/hooks";
import { Toaster } from "@finitless/shared/components";
import { useBusinesses } from "./hooks/useDashboardQueries";
import { useBusinessStore } from "./stores/businessStore";
import AppLayout from "./components/AppLayout";
import AppRouter from "./components/AppRouter";
import ProtectedRoute from "./components/ProtectedRoute";
import ForgotPasswordPage from "./components/pages/ForgotPasswordPage";
import DownloadAppPage from "./components/pages/DownloadAppPage";
import LinkInBioPublicPage from "./components/pages/linkInBio/LinkInBioPublicPage";
import { parsePublicLinkInBioPath } from "./lib/linkInBio/linkInBioPublicPath";
import { AuthLoading } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import { useSyncApiLanguageWithQueries } from "./hooks/useSyncApiLanguageWithQueries";
import BrowserMetadata from "./components/BrowserMetadata";
import "./App.css";

const ORDERING_DARK_LOCKUP =
  "/brand/ordering/1.2.1/logos/ordering-lockup-color-reversed.svg";

if (typeof window !== "undefined") {
  window.clearBusinessState = () => {
    const { clearBusinesses } = useBusinessStore.getState();
    clearBusinesses();
  };
}

// Main App Content Component
const AppContent = () => {
  const { t } = useTranslation("app");
  const { isLoading } = useAuth();
  const location = useLocation();
  const pathname = location.pathname;
  const publicLinkInBioMatch = parsePublicLinkInBioPath(pathname);
  const isPublicLinkInBioRoute = Boolean(publicLinkInBioMatch);

  if (!isPublicLinkInBioRoute && isLoading) {
    return (
      <AuthLoading
        logo={ORDERING_DARK_LOCKUP}
        logoAlt="Ordering.co"
        message={t("authLoading.message")}
        subtitle={t("authLoading.subtitle")}
      />
    );
  }

  if (publicLinkInBioMatch) {
    return (
      <LinkInBioPublicPage
        finitlessProject={publicLinkInBioMatch.project}
        slug={publicLinkInBioMatch.slug}
      />
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/password/forgot" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ForgotPasswordPage />} />
      <Route path="/download-app" element={<DownloadAppPage />} />

      {/* Protected Routes */}
      <Route
        path="*"
        element={
          <GlobalConfigProvider useGlobalConfigs={useGlobalConfigs}>
            <BrandProvider
              useBusinesses={useBusinesses}
              useBusinessStore={useBusinessStore}
            >
              <WebsocketProvider Socket={Socket}>
                <IntercomProvider useIntercom={useIntercom}>
                  <ProtectedRoute>
                    <AppLayout>
                      <AppRouter />
                    </AppLayout>
                  </ProtectedRoute>
                </IntercomProvider>
              </WebsocketProvider>
            </BrandProvider>
          </GlobalConfigProvider>
        }
      />
    </Routes>
  );
};

function App() {
  useSyncApiLanguageWithQueries();

  return (
    <BrowserRouter>
      <QueryProvider>
        <ToastProvider useToast={useToast} Toaster={Toaster}>
          <AuthProvider>
            <BrowserMetadata />
            <PostHogProvider>
              <AppContent />
            </PostHogProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryProvider>
    </BrowserRouter>
  );
}

export default App;
